import { css } from '@compiled/react';
import Link from '@tiptap/extension-link';
import { Doc } from 'components/common/text-editor/useTipTap';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { useEditor, EditorContent } from '@tiptap/react';
import { useRef, useState } from 'react';
import { useIsOverflowing } from 'utils/useIsOverflowing';
import { trackBioLinkClick } from 'analytics/sidePanelAnalytics';
import { Expander } from './Expander';
import { formatUrlForDisplay } from 'utils';

// Giving this a name to avoid the "duplicate link" console warning
const ExtendedLink = Link.extend({ name: 'inclusiveLink', inclusive: false });

export const Bio = ({
  htmlText,
  isPersonalView,
}: {
  htmlText: string;
  isPersonalView: boolean;
}) => {
  const editor = useEditor({
    editable: false,
    editorProps: {
      attributes: {
        class: 'editor',
      },
    },
    content: htmlText,
    extensions: [
      Doc,
      Paragraph,
      Text,
      ExtendedLink.configure({
        openOnClick: false,
        // Example validation:
        // validate: (href) => /^https?:\/\//.test(href),
      }),
    ],
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef: React.MutableRefObject<HTMLElement | null> =
    useRef<HTMLElement | null>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Grab the DOM element for the text content
  if (editor) {
    const $paragraph = editor.$node('paragraph');
    if ($paragraph) {
      contentRef.current = $paragraph.element;
    }
  }
  useIsOverflowing(contentRef, 4, (isOverflowing: boolean) => {
    setIsOverflowing(isOverflowing);
  });

  const onClickEditor = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      event.preventDefault();
      const url = target.getAttribute('href') || '';
      if (url) {
        trackBioLinkClick(isPersonalView);
        window.open(formatUrlForDisplay(url));
      }
    }
  };

  return (
    <div
      css={[bioStyles, !isExpanded && bioOverflowStyles]}
      onClick={onClickEditor}
    >
      <EditorContent editor={editor} />
      {isOverflowing && (
        <Expander expanded={isExpanded} toggleExpanded={toggleExpanded} />
      )}
    </div>
  );
};

const bioStyles = css({
  fontSize: '16px',
  fontFamily: 'inter',
  fontWeight: '400',
  lineHeight: '24px',
});

// Turn off this rule so that we can style TipTap elements
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
const bioOverflowStyles = css({
  '.editor': {
    p: {
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '4',
      overflow: 'hidden',
    },
  },
});
// Turn the rule back on
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */
